import {
    makeStyles 
} from '@mui/styles';
import {
    Box, 
    Typography
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import SessionContext from '../../../contexts/session';
import {
    useContext, useEffect, useState 
} from 'react';
import {
    Data, put 
} from '../../../networking';
import {
    useCookies 
} from 'react-cookie';
import {
    ampLogEvent 
} from '../../../lib/amplitude';
import Phone from '../../authentication/phone';
import Code from '../../authentication/code';
import {
    refreshUserAndStoreIn 
} from '../../../logic/auth';
import {
    gaEvent 
} from '../../../lib/gtag';
import dataDogLogger from '../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants 
} from '../../../figmaTheme';

type Props = {
    position: number
};

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    slide: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100px',
        height: 'calc(100vh - 92px)'
    },
    content: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    container: {
        [theme.breakpoints.up('lg')]: {
            width: '60%'
        },
        height: '100%',
        width: '100%',
        opacity: 0,
        animation: '$fadeIn 0.5s forwards 0s'
    },
    title: {
        marginBottom: '20px',
    },
    subtitle: {
        marginBottom: '44px',
    },
    button: {
        fontSize: '16px',
        lineHeight: '24px',
        color: theme.palette.white,
        padding: '12px 16px',
        fontWeight: 500,
        borderRadius: '16px'
    },
    arrowButton: {
        '&:hover': {
            backgroundColor: theme.palette.tertiary.main,
        },
        backgroundColor: theme.palette.tertiary.main,
        padding: '16px',
        borderRadius: '50%',
        placeSelf: 'end',
        minWidth: 0,
        lineHeight: 0,
        transition: 'opacity 0.5s, pointerEvents 0.5s'
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: '0' 
        },
        '100%': {
            opacity: '1' 
        },
    }
}));

async function putCurrentUser(payload: Data): Promise<Data> {
    return await put('users/current-user', payload);
}

const TITLES = ['Enter your cell phone number to view the plan we\'ve built for you.', 'Verify your phone number to continue to your plan.'];
const SUBTITLES = ['We use text messages as a proven strategy build new habits around your drinking patterns. You can customize or turn them off at any time.', ''];

export const PhoneScreen2 = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const classes = useStyles();
    const [startTime, setStartTime] = useState(formContext.startTime);
    const [step, setStep] = useState(0);
    const [cookies] = useCookies();
    const session = useContext(SessionContext);

    const handlePhoneChange = (phoneNumber) => {
        setStartTime(new Date().getTime());
        if (session.user.phone !== phoneNumber) {
            putCurrentUser({
                phone: phoneNumber 
            });
            session.setUser({
                phone: phoneNumber 
            });
        }
        ampLogEvent(
            'signup: authorized phone number in flow',
            {
                formId: formContext.formId
            },
            cookies
        );
        setStep(1);
    };

    const handleOnChange = async (validated: boolean) => {
        if (validated) {
            await refreshUserAndStoreIn(session);
            formContext.goNext();
        }
    };

    const onBackClick = () => {
        if (step === 0) {
            formContext.goBack(2);
            return;
        }
        setStartTime(null);
        setStep(0);
    };

    useEffect(() => {
        refreshUserAndStoreIn(session);
        gaEvent({
            action: 'Page',
            category: 'Land',
            label: 'signup: landed on phone entry',
            value: '',
        });
        formContext.fireTrackingEvents(formContext, 'phone entry', cookies);
    }, []);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen ${step === 0 ? 'phone entry' : 'code input'} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: step === 0 ? 'phone entry' : 'code input',
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            hideArrowButton
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.slide}>
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                                className={classes.title}
                            >
                                {TITLES[step]}
                            </Typography>
                            {SUBTITLES[step] && (
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                >
                                    {SUBTITLES[step]}
                                </Typography>
                            )}
                            {step === 0 ? (
                                <Box className={classes.container}>
                                    <Phone
                                        mode='register'
                                        onChange={handlePhoneChange}
                                        initialPhone={session?.user?.phone || ''}
                                        isCustom
                                    />
                                </Box>
                            ) : (
                                <Box className={classes.container}>
                                    <Code
                                        submitButtonLabel="See my plan"
                                        mode="register"
                                        autoSubmit={false}
                                        onChange={handleOnChange}
                                        goBack={onBackClick}
                                        isCustom
                                        formId={formContext.formId}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box
                        style={{
                            height: '600px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default PhoneScreen2;
